import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";

interface TabHintProps {
    /** Der Erklärsatz (Text, ggf. mit Inline-Icons). Optional: ohne Text bleibt nur die Aktion rechts. */
    children?: ReactNode;
    /** Optionale rechtsbündige Aktion in derselben Zeile (z.B. Reload-Button). */
    action?: ReactNode;
    /** Zusätzliche Klassen für die Zeile (z.B. "pr-12" als Freiraum für den schwebenden Statistik-Button). */
    className?: string;
}

/**
 * Einheitlicher Erklärsatz über den Tab-Inhalten der Startseite (#19113) — eine zentrale Stelle für
 * Typografie und Abstand zur Tab-Leiste, damit alle Tabs (Meine Bewertungen, Auftragspool, Meine
 * Historie, Korrigieren, Admin inkl. Admin-Historie) identisch aussehen.
 *
 * Die Zeile ist bewusst `items-start`: steht rechts eine Aktion (Reload-Button), ist die höher als
 * die Textzeile — mit `items-center` wurde der Text darin vertikal zentriert und saß dadurch je nach
 * Tab ein paar Pixel tiefer (früher per unterschiedlichen `-top-*`-Nudges gegenkorrigiert). Mit
 * Top-Ausrichtung hängt der Text immer gleich hoch, mit oder ohne Button.
 */
export default function TabHint({children, action, className}: TabHintProps) {
    return (
        <div className={["flex items-start justify-between gap-2 mb-2", className].filter(Boolean).join(" ")}>
            {children != null && children !== false ? (
                <p className="my-0 flex-1 min-w-0 relative top-[3px] text-[1.05rem] text-surface-600 dark:text-surface-300">
                    {children}
                </p>
            ) : (
                <span className="flex-1 min-w-0" />
            )}
            {action}
        </div>
    );
}

/**
 * Kurze „keine Treffer“-Zeile, die ein Startseiten-Tab bei aktivem Freitext ohne Treffer statt Hinweis
 * und leerer Tabelle zeigt (die Badges zeigen, wo es Treffer gibt). Die Klasse `tabFilterNoHits` ist
 * ein e2e-Selektor.
 */
export function NoFilterHitsHint({freeText}: {freeText: string}) {
    const {t} = useTranslation();
    return <TabHint className="tabFilterNoHits">{t("start.tabs.filterNoHits", {filter: freeText})}</TabHint>;
}
